import { useCallback, useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';
// @mui
import {
  Box,
  Card,
  Alert,
  Table,
  Stack,
  Button,
  Switch,
  Dialog,
  Container,
  TableRow,
  TableBody,
  TableCell,
  TableHead,
  TextField,
  Typography,
  IconButton,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  MenuItem,
  TableContainer,
  LinearProgress,
  FormControlLabel,
} from '@mui/material';
// routes
import { PATH_DASHBOARD } from '../../../routes/paths';
// hooks
import useSettings from '../../../hooks/useSettings';
// components
import Page from '../../../components/Page';
import Label from '../../../components/Label';
import Iconify from '../../../components/Iconify';
import HeaderBreadcrumbs from '../../../components/HeaderBreadcrumbs';
// data
import axiosInstance from 'src/utils/axios';
import { APIS } from 'src/utils/serviceUrls';
import ConfirmationDialog from '../shared/ConfirmationDialog';
import useIsFullAdmin from 'src/hooks/useIsFullAdmin';
import useContentReorder from '../content-order/useContentReorder';
import ReorderControls from '../content-order/ReorderControls';
import OrderErrorAlert from '../content-order/OrderErrorAlert';
import { itemErrorText, itemFromConflict, patchBody } from '../content-order/reorderModel';
import {
  HOME_STAT_SOURCES,
  HomeStatSource,
  LiveCounts,
  SOURCE_LABELS,
  effectiveValue,
  isLiveSource,
  liveCountsFrom,
  liveHint,
  previewText,
  sourceOf,
} from './homeStatsModel';

// ----------------------------------------------------------------------

type HomeStat = {
  _id?: string;
  label: string;
  label_ar?: string;
  value: number;
  suffixPlus?: boolean;
  icon?: string;
  // A position, set by the arrows only (PUT /home-stats/reorder); never edited.
  order?: number;
  active?: boolean;
  // 'none' = the typed value; else the API shows the live count (owner task 04).
  source?: HomeStatSource;
  updatedAt?: string;
};

const EMPTY: HomeStat = {
  label: '',
  label_ar: '',
  value: 0,
  suffixPlus: true,
  icon: '',
  active: true,
  source: 'none',
};

// The editable fields of the dialog, as the API stores them.
const fieldsOf = (f: HomeStat) => ({
  label: f.label.trim(),
  label_ar: f.label_ar?.trim() || '',
  value: Number(f.value) || 0,
  suffixPlus: f.suffixPlus !== false,
  icon: f.icon?.trim() || '',
  active: f.active !== false,
  source: sourceOf(f),
});

// Icon picker. `key` is stored on the stat; the nextjs home page maps the SAME
// key to a react-icons glyph. `iconify` is the matching FontAwesome icon used
// for the admin preview (so it looks like what the site renders).
const ICON_OPTIONS: { key: string; label: string; iconify: string }[] = [
  { key: 'students', label: 'Students (graduate)', iconify: 'fa6-solid:user-graduate' },
  { key: 'instructors', label: 'Instructors (teacher)', iconify: 'fa6-solid:chalkboard-user' },
  { key: 'countries', label: 'Countries (globe)', iconify: 'fa6-solid:earth-americas' },
  { key: 'courses', label: 'Courses (book)', iconify: 'fa6-solid:book-open' },
  { key: 'sections', label: 'Sections (layers)', iconify: 'fa6-solid:layer-group' },
  { key: 'hours', label: 'Hours (clock)', iconify: 'fa6-regular:clock' },
  { key: 'users', label: 'Users', iconify: 'fa6-solid:users' },
  { key: 'trophy', label: 'Trophy', iconify: 'fa6-solid:trophy' },
  { key: 'certificate', label: 'Certificate', iconify: 'fa6-solid:certificate' },
  { key: 'star', label: 'Star', iconify: 'fa6-solid:star' },
  { key: 'briefcase', label: 'Briefcase', iconify: 'fa6-solid:briefcase' },
  { key: 'play', label: 'Play', iconify: 'fa6-solid:play' },
];
const iconifyFor = (key?: string) =>
  ICON_OPTIONS.find((o) => o.key === key)?.iconify || 'fa6-solid:star';

// ----------------------------------------------------------------------

export default function HomeStatsList() {
  const { themeStretch } = useSettings();
  const { enqueueSnackbar } = useSnackbar();

  const [rows, setRows] = useState<HomeStat[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState<HomeStat>(EMPTY);
  // The row as the dialog opened it: the base of the changed-fields PATCH and
  // of its version check (expectedUpdatedAt).
  const [base, setBase] = useState<HomeStat | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [deleteState, setDeleteState] = useState<{ open: boolean; id?: string }>({
    open: false,
  });
  const [deleting, setDeleting] = useState(false);
  // The live numbers a tile can follow; null = not loaded (or an older API without the route).
  const [live, setLive] = useState<LiveCounts | null>(null);
  const canDelete = useIsFullAdmin();

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get(APIS.HOME_STATS.LIST);
      const data = res.data?.data ?? res.data;
      setRows(Array.isArray(data) ? data : []);
    } catch (e: any) {
      enqueueSnackbar(e?.message || 'Failed to load stats', { variant: 'error' });
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  // Best effort: without the counts the screen still works, it only cannot say "Right now: N".
  useEffect(() => {
    let cancelled = false;
    axiosInstance
      .get(APIS.HOME_STATS.LIVE_COUNTS)
      .then((res) => {
        if (!cancelled) setLive(liveCountsFrom(res.data?.data ?? res.data));
      })
      .catch(() => {
        if (!cancelled) setLive(null);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Every row is in the one scope (active and hidden), so the answer replaces the list.
  const reorder = useContentReorder<HomeStat>({
    url: APIS.HOME_STATS.REORDER,
    apply: setRows,
    reload: fetchRows,
  });

  // A new stat gets no order: the API appends it after the last one.
  const openNew = () => {
    setForm({ ...EMPTY });
    setBase(null);
    setFormError(null);
    setFormOpen(true);
  };
  const openEdit = (r: HomeStat) => {
    setForm({ ...EMPTY, ...r });
    setBase(r);
    setFormError(null);
    setFormOpen(true);
  };
  const setField = (k: keyof HomeStat, v: any) => setForm((f) => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.label.trim()) {
      enqueueSnackbar('Label is required', { variant: 'warning' });
      return;
    }
    setSaving(true);
    setFormError(null);
    const fields = fieldsOf(form);
    try {
      // An edit sends only what changed (+ the version it opened), never `order`.
      if (form._id && base) {
        const body = patchBody(
          'homeStat',
          { ...fieldsOf({ ...EMPTY, ...base }), updatedAt: base.updatedAt },
          fields
        );
        if (Object.keys(body).every((k) => k === 'expectedUpdatedAt')) {
          setFormOpen(false);
          return;
        }
        await axiosInstance.patch(APIS.HOME_STATS.PATCH(form._id), body);
      } else await axiosInstance.post(APIS.HOME_STATS.ADD, fields);
      enqueueSnackbar(form._id ? 'Stat updated' : 'Stat created');
      setFormOpen(false);
      // The fresh `updatedAt` of the row is the token of its next edit.
      await fetchRows();
    } catch (e: any) {
      // Someone changed this stat meanwhile: their version goes into the form.
      const latest = itemFromConflict<HomeStat>(e);
      if (latest) {
        setBase(latest);
        setForm({ ...EMPTY, ...latest });
      }
      setFormError(itemErrorText(e));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteState.id) return;
    setDeleting(true);
    try {
      await axiosInstance.delete(APIS.HOME_STATS.DELETE(deleteState.id));
      enqueueSnackbar('Stat deleted');
      setDeleteState({ open: false });
      await fetchRows();
    } catch (e: any) {
      enqueueSnackbar(e?.message || 'Delete failed', { variant: 'error' });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Page title="Home Statistics">
      <Container maxWidth={themeStretch ? false : 'lg'}>
        <HeaderBreadcrumbs
          heading="Home Statistics"
          links={[
            { name: 'Dashboard', href: PATH_DASHBOARD.root },
            { name: 'Home Statistics' },
          ]}
          action={
            <Button
              variant="contained"
              startIcon={<Iconify icon="eva:plus-fill" />}
              onClick={openNew}
            >
              New stat
            </Button>
          }
        />

        <Card sx={{ p: 2.5, mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            These numbers drive the “Numbers” section on the public home page. A stat can follow a{' '}
            <b>live number</b> (active instructors, countries of our students, published Kayfa courses)
            so it never goes out of date, or show the value you type. With a trailing “+” the site
            rounds the number down to a round figure (e.g. 15,237 shows as <b>15,000+</b>). Use the
            arrows to change the order. It is saved at once and shows on the site right away. Hide a
            row by turning off Active. When no rows are active the site falls back to live-computed
            counts.
          </Typography>
        </Card>

        <OrderErrorAlert error={reorder.error} saving={reorder.saving} onClose={reorder.clearError} />

        <Card>
          <TableContainer sx={{ position: 'relative', overflowX: 'auto' }}>
            {loading && (
              <LinearProgress
                sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, zIndex: 9 }}
              />
            )}
            <Table sx={{ minWidth: 720 }}>
              <TableHead>
                <TableRow>
                  <TableCell align="center" sx={{ width: 56 }}>
                    #
                  </TableCell>
                  <TableCell sx={{ width: 120 }}>Order</TableCell>
                  <TableCell>Label</TableCell>
                  <TableCell>Arabic</TableCell>
                  <TableCell align="center">Displays as</TableCell>
                  <TableCell align="center">Active</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {!loading && rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                      No stats yet — click “New stat” to add one.
                    </TableCell>
                  </TableRow>
                )}
                {rows.map((r, i) => (
                  <TableRow key={r._id} hover>
                    <TableCell align="center">{i + 1}</TableCell>
                    <TableCell>
                      <ReorderControls
                        name={r.label || `stat ${i + 1}`}
                        index={i}
                        count={rows.length}
                        disabled={loading || reorder.saving}
                        onMove={(kind) => reorder.move(rows, i, kind)}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="subtitle2">{r.label}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary" dir="rtl">
                        {r.label_ar || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Stack direction="row" alignItems="center" justifyContent="center" spacing={1}>
                        {r.icon ? (
                          <Iconify
                            icon={iconifyFor(r.icon)}
                            sx={{ width: 18, height: 18, color: 'primary.main' }}
                          />
                        ) : null}
                        <Label color="info">{previewText(r, live)}</Label>
                        {isLiveSource(r.source) ? (
                          <Label
                            color={effectiveValue(r, live).live ? 'success' : 'warning'}
                            title={SOURCE_LABELS[sourceOf(r)]}
                          >
                            Live
                          </Label>
                        ) : null}
                      </Stack>
                    </TableCell>
                    <TableCell align="center">
                      <Label color={r.active !== false ? 'success' : 'default'}>
                        {r.active !== false ? 'Active' : 'Hidden'}
                      </Label>
                    </TableCell>
                    <TableCell align="right">
                      <IconButton onClick={() => openEdit(r)}>
                        <Iconify icon="eva:edit-fill" />
                      </IconButton>
                      {/* DELETE is admin-only on the backend; create/edit are
                          open to customer-care and sales. */}
                      {canDelete && (
                        <IconButton
                          sx={{ color: 'error.main' }}
                          onClick={() => setDeleteState({ open: true, id: r._id })}
                        >
                          <Iconify icon="eva:trash-2-outline" />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      </Container>

      {/* Add / edit dialog */}
      <Dialog open={formOpen} onClose={() => setFormOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{form._id ? 'Edit stat' : 'New stat'}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2.5} sx={{ pt: 1 }}>
            {formError && (
              <Alert severity="error" onClose={() => setFormError(null)}>
                {formError}
              </Alert>
            )}
            <TextField
              label="Label (English)"
              value={form.label}
              onChange={(e) => setField('label', e.target.value)}
              fullWidth
              required
            />
            <TextField
              label="Label (Arabic)"
              value={form.label_ar || ''}
              onChange={(e) => setField('label_ar', e.target.value)}
              fullWidth
              inputProps={{ dir: 'rtl' }}
            />
            <TextField
              select
              label="Number comes from"
              value={sourceOf(form)}
              onChange={(e) => setField('source', e.target.value)}
              fullWidth
              helperText={liveHint(sourceOf(form), live)}
            >
              {HOME_STAT_SOURCES.map((src) => (
                <MenuItem key={src} value={src}>
                  {SOURCE_LABELS[src]}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label={isLiveSource(form.source) ? 'Typed value (backup)' : 'Value'}
              type="number"
              value={form.value}
              onChange={(e) => setField('value', e.target.value)}
              fullWidth
              InputProps={{
                endAdornment:
                  form.suffixPlus !== false ? (
                    <InputAdornment position="end">+</InputAdornment>
                  ) : undefined,
              }}
              helperText={
                isLiveSource(form.source)
                  ? 'Shown only when the live number cannot be read.'
                  : form._id
                  ? undefined
                  : 'A new stat is added at the end. Use the arrows in the list to move it.'
              }
            />
            <TextField
              select
              label="Icon"
              value={form.icon || ''}
              onChange={(e) => setField('icon', e.target.value)}
              fullWidth
            >
              <MenuItem value="">
                <em>No icon</em>
              </MenuItem>
              {ICON_OPTIONS.map((o) => (
                <MenuItem key={o.key} value={o.key}>
                  <Stack direction="row" alignItems="center" spacing={1.5}>
                    <Iconify icon={o.iconify} sx={{ width: 20, height: 20 }} />
                    <span>{o.label}</span>
                  </Stack>
                </MenuItem>
              ))}
            </TextField>
            <FormControlLabel
              control={
                <Switch
                  checked={form.suffixPlus !== false}
                  onChange={(e) => setField('suffixPlus', e.target.checked)}
                />
              }
              label='Show "+" after the number'
            />
            <FormControlLabel
              control={
                <Switch
                  checked={form.active !== false}
                  onChange={(e) => setField('active', e.target.checked)}
                />
              }
              label="Active (visible on the site)"
            />
            <Box sx={{ p: 2, borderRadius: 1.5, bgcolor: 'background.neutral', textAlign: 'center' }}>
              {form.icon ? (
                <Iconify
                  icon={iconifyFor(form.icon)}
                  sx={{ width: 28, height: 28, color: 'primary.main', mb: 0.5 }}
                />
              ) : null}
              <Typography variant="h3">{previewText(form, live)}</Typography>
              <Typography variant="caption" color="text.secondary">
                {form.label || 'Label'}
              </Typography>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button color="inherit" onClick={() => setFormOpen(false)}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmationDialog
        isOpen={deleteState.open}
        onClose={() => setDeleteState({ open: false })}
        confirmAction={handleDelete}
        headText="Delete stat"
        text="Are you sure you want to delete this home statistic?"
        loading={deleting}
      />
    </Page>
  );
}
